import { KeyboardEvent } from 'react';
import * as R from 'ramda';
import { v4 as uuid } from 'uuid';

import { DataType } from '@atom/types/dataType';
import {
  Task,
  TaskDropdownField,
  TaskField,
  TaskUserStatus,
} from '@atom/types/task';
import {
  SubField,
  TaskRequiredValidation,
  ValueDictionary,
  WorkOrderBulkField,
  WorkOrderDetailType,
  WorkOrderDropdownField,
  WorkOrderField,
} from '@atom/types/work';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import { isWorkOrderCompletedOrAbove } from './workOrderStatusUtilities';

export const MIN_SINGLE_SELECT_OPTIONS = 1;

// Option inputs wrap visually, but saved values must stay on one line.
// - Call this to remove line breaks and extra whitespace from a string before saving.
export const toSingleLineOption = (value: string): string =>
  value.replace(/\s*[\r\n]+\s*/g, ' ');

export const preventOptionLineBreak = (event: KeyboardEvent<any>) => {
  if (event.key === 'Enter') {
    event.preventDefault();
  }
};

// Data types that can be displayed as a list instead of a dropdown via `viewAs`
export const VIEW_AS_DATA_TYPES = [
  DataType.ENUM_SINGLE,
  DataType.ENUM_MULTIPLE,
];

export const MAX_LIST_OPTIONS = 20;

export const isOverListOptionLimit = (optionCount: number): boolean =>
  optionCount > MAX_LIST_OPTIONS;

export const initialNestedField: WorkOrderDropdownField = {
  title: '',
  dataType: DataType.ENUM_SINGLE,
  enumeration: ['', ''],
  defaultValue: null,
  subFields: [],
};

export const getPropertyByValue = (
  match: string,
  valueDictionary: ValueDictionary,
) => {
  const property = R.keys(valueDictionary).find(
    (key: string): boolean => match === valueDictionary[key],
  );

  return property ? property.toString() : null;
};

// Creates a valueDictionary of UUID to enumeration value and replaces all
// enumeration, matchValue, and defaultValue down an infinite tree with the UUID.
export const mapDropdownField = (
  field: WorkOrderDropdownField | TaskDropdownField,
): any => {
  let valueDictionary = {};

  const mappedEnumeration = field.enumeration.map((enumItem: string) => {
    const id = uuid();

    valueDictionary = {
      ...valueDictionary,
      [id]: enumItem,
    };

    return id;
  });

  const mappedDefaultValue = getPropertyByValue(
    field.defaultValue,
    valueDictionary,
  );

  const mappedSubFields = field.subFields
    ? field.subFields.map((subField: SubField): SubField => {
        const mappedMatchValue = getPropertyByValue(
          subField.matchValue,
          valueDictionary,
        );

        const mappedNestedField = mapDropdownField(subField.nestedField);

        valueDictionary = {
          ...valueDictionary,
          ...mappedNestedField.valueDictionary,
        };

        return {
          matchValue: mappedMatchValue,
          nestedField: R.omit(['valueDictionary'], mappedNestedField),
        };
      })
    : [];

  return {
    ...field,
    valueDictionary,
    enumeration: mappedEnumeration,
    defaultValue: mappedDefaultValue,
    subFields: mappedSubFields,
  };
};

// Replaces all enumeration, matchValue, defaultValue with the matching value
// from the passed in valueDictionary in an infinite tree. It also hydrates
// the required value to all tree nodes if that value is passed in
export const hydrateDropdownField = (
  field: any,
  valueDictionary: ValueDictionary,
  required?: boolean,
  requiredOnCreate?: boolean,
) => {
  const hydratedEnumeration = field.enumeration.map(
    (key: string): string => valueDictionary[key],
  );

  const hydratedSubFields = field.subFields.map(
    (subField: SubField): SubField => {
      const hydratedNestedField = hydrateDropdownField(
        subField.nestedField,
        valueDictionary,
        required,
        requiredOnCreate,
      );

      return {
        matchValue: valueDictionary[subField.matchValue],
        nestedField: R.omit(['value'], hydratedNestedField),
      };
    },
  );

  return {
    ...field,
    enumeration: hydratedEnumeration,
    defaultValue: field.defaultValue
      ? valueDictionary[field.defaultValue]
      : null,
    subFields: hydratedSubFields,
    ...(!R.isNil(required) && { required }),
    ...(!R.isNil(requiredOnCreate) && { requiredOnCreate }),
  };
};

export const getNestedPath = (
  matchValue: string,
  subFields: SubField[],
  previousPath: any[],
): any[] => {
  return subFields.reduce((acc: any[], subField: SubField, index: number) => {
    const currentPath = [...previousPath, 'nestedField', 'subFields', index];

    return subField.matchValue === matchValue
      ? [...acc, ...currentPath]
      : [
          ...acc,
          ...getNestedPath(
            matchValue,
            subField.nestedField.subFields || [],
            currentPath,
          ),
        ];
  }, []);
};

export const getPath = (matchValue: string, subFields: SubField[]): any[] => {
  return subFields.reduce((acc: any[], subField: SubField, index: number) => {
    const currentPath = [index];

    return subField.matchValue === matchValue
      ? [...acc, ...currentPath]
      : [
          ...acc,
          ...getNestedPath(
            matchValue,
            subField.nestedField.subFields || [],
            currentPath,
          ),
        ];
  }, []);
};

// Takes a subField object and replaces the matching subField in the
// root subFields array.
export const updateSubFields = (
  subField: SubField,
  subFields: SubField[],
): SubField[] => {
  const path = getPath(subField.matchValue, subFields);
  return R.set(
    R.lensPath(path),
    R.omit(['value'], subField as Record<string, any>),
    subFields,
  );
};

export const getNestedSubFieldsPath = (
  matchValue: string,
  subFields: SubField[],
  previousPath: any[],
): any[] => {
  return subFields.reduce((acc: any[], subField: SubField, index: number) => {
    const currentPath = [...previousPath, 'nestedField', 'subFields'];

    return subField.matchValue === matchValue
      ? [...acc, ...currentPath]
      : [
          ...acc,
          ...getNestedSubFieldsPath(
            matchValue,
            subField.nestedField.subFields || [],
            [...currentPath, index],
          ),
        ];
  }, []);
};

export const getSubFieldsPath = (
  matchValue: string,
  subFields: SubField[],
): any[] => {
  return subFields.reduce((acc: any[], subField: SubField, index: number) => {
    const currentPath = [];

    return subField.matchValue === matchValue
      ? [...acc, ...currentPath]
      : [
          ...acc,
          ...getNestedSubFieldsPath(
            matchValue,
            subField.nestedField.subFields || [],
            [...currentPath, index],
          ),
        ];
  }, []);
};

export const removeSubFieldByMatchValue = (
  subFields: SubField[],
  key: string,
): SubField[] => {
  const path = getSubFieldsPath(key, subFields);
  return R.over(
    R.lensPath(path),
    nestedSubFields => {
      const matchValueIndex = R.findIndex(R.propEq(key, 'matchValue'))(
        nestedSubFields,
      );

      return matchValueIndex < 0
        ? nestedSubFields
        : R.remove(matchValueIndex, 1, nestedSubFields);
    },
    subFields,
  );
};

// Removes the subField linked to a removed option. A single-select left with
// only one option can't cascade, so any remaining subFields are cleared too.
export const removeOptionSubFields = (
  subFields: SubField[],
  key: string,
  remainingOptionCount: number,
): SubField[] =>
  remainingOptionCount <= MIN_SINGLE_SELECT_OPTIONS
    ? []
    : removeSubFieldByMatchValue(subFields, key);

// Returns the full breadcrumb path for cascading dropdowns in the
// form of Title: option / Title: option ? etc
export const getActiveSubFieldPathLabel = (
  rootTitle: string,
  activePath: any[],
  subFields: SubField[],
  valueDictionary: ValueDictionary,
): string => {
  if (R.isEmpty(activePath)) {
    return '';
  }

  const selectedRootOption = subFields[R.head(activePath)].matchValue;
  const rootLabel = `${rootTitle}: ${valueDictionary[selectedRootOption]}`;

  const groupedNestedActivePath = R.splitEvery(3, R.drop(1, activePath));

  const nestedLabels = groupedNestedActivePath.map(
    (nestedPath: any[], index: number) => {
      const pathToSubField = [
        activePath[0],
        ...R.flatten(R.slice(0, index + 1, groupedNestedActivePath)),
      ];
      const pathToNestedField = R.dropLast(2, pathToSubField);

      const field: any = R.view(R.lensPath(pathToNestedField), subFields);
      const subField: SubField = R.view(R.lensPath(pathToSubField), subFields);

      return `${field.title}: ${valueDictionary[subField.matchValue]}`;
    },
  );

  return [rootLabel, ...nestedLabels].join(' / ');
};

// Returns the depth of the current nestedField that
// matches the passed in matchValue.
export const getNestedDepth = (matchValue: string, subFields: SubField[]) => {
  const currentPath = getPath(matchValue, subFields);

  // Each grouping of 3 items in the path represents a full cascaded
  // dropdown child, thus splitting by every 3 will produce an
  // array with length equal to nested depth.
  return R.splitEvery(3, currentPath).length;
};

export const getNestedLeafPath = (field: TaskField): any[] => {
  const subFieldIndex = R.findIndex(R.propEq(field.value, 'matchValue'))(
    field.subFields || [],
  );

  if (subFieldIndex < 0) {
    return [];
  }

  return [
    'subFields',
    subFieldIndex,
    'nestedField',
    ...getNestedLeafPath(field.subFields[subFieldIndex].nestedField),
  ];
};

export const getCascadingDropdownLeafNode = (field: TaskField) => {
  const subFieldIndex = R.findIndex(R.propEq(field.value, 'matchValue'))(
    field.subFields || [],
  );

  if (subFieldIndex < 0) {
    return field;
  }

  const path = [
    'subFields',
    subFieldIndex,
    'nestedField',
    ...getNestedLeafPath(field.subFields[subFieldIndex].nestedField),
  ];

  return R.view(R.lensPath(path), field);
};

// The id to flag for an invalid field: the field itself, or for a cascading
// single select, the level of the selected cascade still missing a value.
export const getInvalidFieldId = (
  field: TaskField | WorkOrderField | WorkOrderBulkField,
): string =>
  field.dataType === DataType.ENUM_SINGLE && !isNilOrEmpty(field.subFields)
    ? (getCascadingDropdownLeafNode(field as TaskField)?.id ?? field.id)
    : field.id;

// Cascading single selects are validated at the deepest level of the
// selected cascade, which is the first level still missing a value.
export const getCascadeValidationTarget = <
  T extends TaskField | WorkOrderField | WorkOrderBulkField,
>(
  field: T,
): T =>
  field.dataType === DataType.ENUM_SINGLE && !isNilOrEmpty(field.subFields)
    ? (getCascadingDropdownLeafNode(field as TaskField) as T)
    : field;

// Ids of a field and every nested cascading field beneath it.
export const getFieldTreeIds = (
  field: TaskField | WorkOrderField | WorkOrderBulkField,
): string[] => [
  field.id,
  ...(field.subFields || []).flatMap(({ nestedField }) =>
    getFieldTreeIds(nestedField),
  ),
];

// Checks if the given field is invalid
// If a field is not required, it is valid
// If a field is required, it's value must pass dataType specific
// tests in order to be valid
export const isFieldInvalid = (field: TaskField): boolean => {
  if (!field?.required) {
    return false;
  }

  if (
    field.dataType === DataType.ENUM_SINGLE &&
    !isNilOrEmpty(field.subFields)
  ) {
    const fieldLeafNode = getCascadingDropdownLeafNode(field);

    // @ts-ignore
    return isNilOrEmpty(fieldLeafNode?.value);
  }

  // Multi selects set to "select all" need every option selected
  if (
    field.dataType === DataType.ENUM_MULTIPLE &&
    field.selectAllOnCompleteWork
  ) {
    return (
      isNilOrEmpty(field.value) ||
      field.enumeration?.length !== field.value?.length
    );
  }

  return isNilOrEmpty(field?.value);
};

// Validate required fields checks if all custom fields on tasks
// are valid according to dataType specific rules in relation to the required boolean.
// If a taskId is passed, validation will only be run on that
// specific task, otherwise it is run on all tasks on the given workOrder.
// * Added 5.2.24 - if status is below completed, skip required field check
export const validateTaskRequiredFields = (
  workOrderDetail: WorkOrderDetailType,
  taskId?: string,
): TaskRequiredValidation => {
  if (
    isNilOrEmpty(workOrderDetail.tasks) ||
    !isWorkOrderCompletedOrAbove(workOrderDetail.statusId)
  ) {
    return {
      isValid: true,
      invalidTaskFields: {},
    };
  }

  const taskList = taskId
    ? workOrderDetail.tasks.filter(task => task.id === taskId)
    : workOrderDetail.tasks;

  const invalidTaskFields = taskList.reduce((acc, task) => {
    if (isNilOrEmpty(task.fields)) {
      return acc;
    }

    const invalidFieldIds = task.fields.reduce((accumulator, field) => {
      return isFieldInvalid(field)
        ? [...accumulator, getInvalidFieldId(field)]
        : accumulator;
    }, []);

    return {
      ...acc,
      ...(!isNilOrEmpty(invalidFieldIds) && {
        [task.id]: new Set(invalidFieldIds),
      }),
    };
  }, {});

  return {
    isValid: isNilOrEmpty(R.keys(invalidTaskFields)),
    invalidTaskFields,
  };
};

export const getNestedFieldPathById = (
  id: string,
  subFields: SubField[],
  previousPath: any[],
): any[] => {
  return subFields.reduce((acc: any[], subField: SubField, index: number) => {
    const currentPath = [...previousPath, 'subFields', index, 'nestedField'];

    return subField.nestedField.id === id
      ? [...acc, ...currentPath]
      : [
          ...acc,
          ...getNestedFieldPathById(
            id,
            subField.nestedField.subFields || [],
            currentPath,
          ),
        ];
  }, []);
};

export const getFieldPathById = (id: string, fields: any[]): any[] => {
  return fields.reduce((acc: any[], field: any, index: number) => {
    const currentPath = ['fields', index];

    return field.id === id
      ? [...acc, ...currentPath]
      : [
          ...acc,
          ...getNestedFieldPathById(id, field.subFields || [], currentPath),
        ];
  }, []);
};

// Finds the top-level field containing the given (possibly nested) field id.
export const getRootFieldById = <
  T extends TaskField | WorkOrderField | WorkOrderBulkField,
>(
  fieldId: string,
  fields: T[],
): T | undefined => {
  const path = getFieldPathById(fieldId, fields);

  return isNilOrEmpty(path) ? undefined : fields[path[1]];
};

export const isLastUserToComplete = (task: Task): boolean => {
  const usersNotComplete = task.users.filter(
    user => user.status !== TaskUserStatus.COMPLETED,
  );

  return usersNotComplete.length === 1;
};

export const isCascadingField = (field: WorkOrderField | TaskField) => {
  return !R.isEmpty(field?.subFields) || field?.subFieldsTruncated;
};

const isRequiredFieldValueMissing = (
  field: WorkOrderField | WorkOrderBulkField,
  selectAllFieldName: 'selectAllOnCreateWork' | 'selectAllOnCompleteWork',
) => {
  if (field.dataType !== DataType.ENUM_MULTIPLE) {
    return isNilOrEmpty(field.value);
  }

  return field[selectAllFieldName]
    ? isNilOrEmpty(field.value) ||
        field?.enumeration?.length !== field?.value?.length
    : isNilOrEmpty(field.value);
};

export const isRequiredCreateFieldInvalid = (
  field: WorkOrderField | WorkOrderBulkField,
) => isRequiredFieldValueMissing(field, 'selectAllOnCreateWork');

// Checks if a field marked `requiredOnCreate` is missing a value, checking
// the selected level of a cascading single select.
export const isRequiredOnCreateFieldInvalid = (
  field: WorkOrderField | WorkOrderBulkField,
) =>
  !!field.requiredOnCreate &&
  isRequiredCreateFieldInvalid(getCascadeValidationTarget(field));

// Checks if a field marked `required` (i.e. required on complete/close) is
// missing a value, once the work order reaches a Completed or Closed status.
export const isRequiredCompleteFieldInvalid = (
  field: WorkOrderField | WorkOrderBulkField,
) => isRequiredFieldValueMissing(field, 'selectAllOnCompleteWork');

// Maps each work order field requirement to the flag that decides whether a
// multi-select needs every option selected to satisfy it.
const SELECT_ALL_FIELD_NAMES: Partial<
  Record<
    keyof WorkOrderField,
    'selectAllOnCreateWork' | 'selectAllOnCompleteWork'
  >
> = {
  requiredOnCreate: 'selectAllOnCreateWork',
  required: 'selectAllOnCompleteWork',
};

export const getMultiSelectRequirementText = (selectAll: boolean) =>
  selectAll ? '(Required to select all)' : '(Required to select any)';

export const getRequiredMultiSelectTitle = (
  title: string,
  selectAll: boolean,
) => `* ${title} ${getMultiSelectRequirementText(selectAll)}`;

export const getWorkOrderMultiSelectTitle = (
  field: WorkOrderField,
  requiredFieldNames: (keyof WorkOrderField)[],
) => {
  const activeNames = requiredFieldNames.filter(name => !!field[name]);

  return R.isEmpty(activeNames)
    ? field.title
    : getRequiredMultiSelectTitle(
        field.title,
        activeNames.some(name => !!field[SELECT_ALL_FIELD_NAMES[name]]),
      );
};
